<script setup>
import { onMounted, onUnmounted, ref, watch, computed, nextTick } from 'vue'
import { useResultsStore } from '../stores/results-fetcher.js'
import GenderFilter from './GenderFilter.vue'
import { useStateStore } from '../stores/state.js'
import { useRoute } from "vue-router";
import { formatGapTime } from '../utils/gap-formatter.js'
import { formatMarkerText } from '../utils/marker-formatter.js'
import EventSwitcher from './EventSwitcher.vue'

const resultsStore = useResultsStore()
const stateStore = useStateStore()
const route = useRoute()
const eventAlias = route.params.eventAlias
resultsStore.fetchResults(eventAlias)
resultsStore.fetchEventDetails(eventAlias)
stateStore.fetchEvents()

const now = ref(Date.now())
let refreshResultsTimer = setInterval(() => {
      resultsStore.fetchResults(route.params.eventAlias)
    }, 30000)
let timer = null

// Search functionality
const searchQuery = ref('')
const showDropdown = ref(false)
const selectedResultId = ref(null)

// Track recently changed rows by result.id and the timestamp of change
const recentlyChanged = ref({})
const showElapsedTime = ref(true)

// Helper to compare results and mark changed rows
let lastResults = []

watch(
  () => resultsStore.results.map(r => ({ id: r.id, score: r.score, finish_time: r.finish_time, finish_time_gps: r.finish_time_gps, dnf: r.dnf, abs: r.abs, categoryPosition: r.categoryPosition, penaltySeconds: r.penaltySeconds, penaltyComment: r.penaltyComment })),
  (newResults) => {
    const nowTime = Date.now()
    if (lastResults.length === 0) {
      lastResults = JSON.parse(JSON.stringify(newResults))
      return
    }
    newResults.forEach((r) => {
      const prev = lastResults.find(lr => lr.id === r.id)
      if (
        !prev ||
        prev.score !== r.score ||
        prev.finish_time !== r.finish_time ||
        prev.finish_time_gps !== r.finish_time_gps ||
        prev.dnf !== r.dnf ||
        prev.abs !== r.abs ||
        prev.categoryPosition !== r.categoryPosition
      ) {
        recentlyChanged.value[r.id] = nowTime
      }
    })
    lastResults = JSON.parse(JSON.stringify(newResults))
  },
  { immediate: true }
)

// Track position changes (rows moving up/down as the potential order shifts live)
const positionChanges = ref({})
let lastPositions = {}

// Computed property for filtered results based on search
const filteredResults = computed(() => {
  if (!searchQuery.value.trim()) {
    return []
  }

  const query = searchQuery.value.trim().toLowerCase()
  return resultsStore.results.filter(result => {
    const nameMatch = result.name.toLowerCase().includes(query)
    const numberMatch = result.number.toString().includes(query)
    return (nameMatch || numberMatch) && result.category_id !== 10
  }).slice(0, 10)
})

watch(searchQuery, (newQuery) => {
  showDropdown.value = newQuery.trim().length > 0 && filteredResults.value.length > 0
})

function selectResult(result) {
  selectedResultId.value = result.id
  searchQuery.value = ''
  showDropdown.value = false

  recentlyChanged.value[result.id] = Date.now()

  nextTick(() => {
    const resultRow = document.querySelector(`tr[data-result-id="${result.id}"]`)
    if (resultRow) {
      resultRow.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }
  })
}

function handleClickOutside(event) {
  if (!event.target.closest('.search-container')) {
    showDropdown.value = false
  }
}

const CLEANUP_INTERVAL = 1000
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  timer = setInterval(() => {
    now.value = Date.now()
    const cutoff = Date.now() - 30 * 1000
    for (const id in recentlyChanged.value) {
      if (recentlyChanged.value[id] < cutoff) {
        delete recentlyChanged.value[id]
      }
    }
    const positionCutoff = Date.now() - 10 * 1000
    for (const id in positionChanges.value) {
      if (positionChanges.value[id].time < positionCutoff) {
        delete positionChanges.value[id]
      }
    }
  }, CLEANUP_INTERVAL)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  if (timer) clearInterval(timer)
  if (refreshResultsTimer) clearInterval(refreshResultsTimer)
})


function calculateTimeFromStart(result) {
  const startTime = new Date(result.start_time)
  const elapsed = new Date(now.value - startTime)
  return getHHMMSSString(elapsed)
}

function timeFromStartToSplit(result, split) {
    const startTime = new Date(result.start_time)
    const splitTime = new Date(split.time)
    const elapsed = new Date(splitTime - startTime)
    return elapsed
}

function getHHMMSSString(date) {
  const timestamp = date instanceof Date ? date.getTime() : (typeof date === 'number' ? date : 0)
  if (timestamp <= 0) {
    return ''
  }
  const hours = Math.floor(timestamp / (1000 * 60 * 60))
  const dateObj = date instanceof Date ? date : new Date(timestamp)
  const minutes = String(dateObj.getUTCMinutes()).padStart(2, '0')
  const seconds = String(dateObj.getUTCSeconds()).padStart(2, '0')
  return `${hours}:${minutes}:${seconds}`
}

function getResultClass(result) {
  if (result.dnf) return 'badge bg-danger'
  if (result.abs) return 'badge bg-secondary'
  if (result.finish_time || result.finish_time_gps) return ''
  return 'result-upcoming'
}

function getMarkersAsArray() {
  if (!resultsStore.markersMap) return []
  return Object.values(resultsStore.markersMap).sort((a, b) => a.sort - b.sort)
}

function isLastMarkerFinish() {
  const markers = getMarkersAsArray()
  if (markers.length === 0) return false
  const lastMarker = markers[markers.length - 1]
  return lastMarker.type == 8 || lastMarker.type == 93
}

function isRunnerLive(result) {
  return !result.dnf && !result.abs && !result.finish_time && !result.finish_time_gps && !!result.start_time && now.value > new Date(result.start_time).getTime()
}

// Number of checkpoint markers a runner has reached so far (finished runners
// count as having passed every marker, plus the finish itself).
function progressLevel(result) {
  if (result.finish_time || result.finish_time_gps) {
    return getMarkersAsArray().length + 1
  }
  return result.unfinishedSplitCount || 0
}

// Elapsed time (ms) for `result` at a given progress level: either the split
// time at that checkpoint, or the final result time if the level is "finish".
function elapsedAtLevel(result, level) {
  const markers = getMarkersAsArray()
  if (level <= markers.length) {
    const marker = markers[level - 1]
    const split = result.splits ? result.splits[marker.id] : null
    if (!split || !split.time) return Infinity
    return timeFromStartToSplit(result, split)
  }
  if (result.finish_time || result.finish_time_gps) {
    return result.elapsedTime != null ? result.elapsedTime : Infinity
  }
  return Infinity
}

// Live elapsed time "right now" for a runner still on course - this is what
// makes the ranking shift over time even without a new punch coming in.
function liveElapsedNow(result, nowTime) {
  if (!result.start_time) return Infinity
  const start = new Date(result.start_time).getTime()
  if (nowTime <= start) return Infinity
  return nowTime - start
}

// Compares two runners by their *potential* current standing: a runner who
// reached fewer checkpoints but did so faster stays ahead of a more advanced
// runner for as long as their live elapsed time is still under that runner's
// recorded time at the next checkpoint ahead.
function comparePotential(a, b, nowTime) {
  if (a.abs) return 1
  if (b.abs) return -1
  if (a.dnf) return 1
  if (b.dnf) return -1

  const pA = progressLevel(a)
  const pB = progressLevel(b)
  const finishLevel = getMarkersAsArray().length + 1

  if (pA === pB) {
    if (pA === finishLevel) {
      if (a.elapsedTime != null && b.elapsedTime != null) {
        return a.elapsedTime - b.elapsedTime
      }
      return a.number - b.number
    }
    if (pA === 0) {
      const aLive = liveElapsedNow(a, nowTime)
      const bLive = liveElapsedNow(b, nowTime)
      if (aLive !== bLive) return aLive - bLive
      return a.number - b.number
    }
    const marker = getMarkersAsArray()[pA - 1]
    const aSplit = a.splits ? a.splits[marker.id] : null
    const bSplit = b.splits ? b.splits[marker.id] : null
    const aElapsed = aSplit ? timeFromStartToSplit(a, aSplit) : Infinity
    const bElapsed = bSplit ? timeFromStartToSplit(b, bSplit) : Infinity
    if (aElapsed !== bElapsed) return aElapsed - bElapsed
    return a.number - b.number
  }

  const lessIsA = pA < pB
  const less = lessIsA ? a : b
  const more = lessIsA ? b : a
  const lessLevel = lessIsA ? pA : pB

  const lessCurrent = liveElapsedNow(less, nowTime)
  const refElapsed = elapsedAtLevel(more, lessLevel + 1)

  const lessStaysAhead = lessCurrent < refElapsed
  if (lessStaysAhead) {
    return lessIsA ? -1 : 1
  }
  return lessIsA ? 1 : -1
}

function comparePointsFirst(a, b) {
  if (a.abs) return 1
  if (b.abs) return -1
  if (a.dnf) return 1
  if (b.dnf) return -1

  const aPoints = a.pointScore || 0
  const bPoints = b.pointScore || 0
  if (aPoints !== bPoints) {
    return bPoints - aPoints
  }
  if (a.elapsedTime != null && b.elapsedTime != null) {
    return a.elapsedTime - b.elapsedTime
  }
  if (a.finish_time || a.finish_time_gps) return -1
  if (b.finish_time || b.finish_time_gps) return 1
  return a.number - b.number
}

const genderFilter = ref('all')
const rankedResults = computed(() => resultsStore.filterByGender(allRankedResults.value, genderFilter.value))
const allRankedResults = computed(() => {
  const nowTime = now.value
  const list = resultsStore.results.filter(r => r.category_id !== 10)
  const hasPointScores = list.some(r => r.pointScore !== null && r.pointScore > 0)
  if (hasPointScores) {
    return list.slice().sort(comparePointsFirst)
  }
  return list.slice().sort((a, b) => comparePotential(a, b, nowTime))
})

watch(
  () => rankedResults.value.map(r => r.id),
  (ids) => {
    const newPositions = {}
    ids.forEach((id, index) => { newPositions[id] = index })
    const nowTime = Date.now()
    Object.keys(newPositions).forEach((id) => {
      const prevIndex = lastPositions[id]
      if (prevIndex !== undefined && prevIndex !== newPositions[id]) {
        positionChanges.value[id] = { dir: newPositions[id] < prevIndex ? 'up' : 'down', time: nowTime }
      }
    })
    lastPositions = newPositions
  },
  { immediate: true }
)

function formatPenaltyLabel(penaltySeconds) {
  const totalSeconds = Math.round(Number(penaltySeconds) || 0)
  if (totalSeconds <= 0) return ''

  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (hours > 0) {
    const minutesPart = minutes > 0 ? `${minutes}${seconds > 0 ? `:${String(seconds).padStart(2, '0')}` : ''}min` : ''
    const secondsPart = minutes === 0 && seconds > 0 ? `${seconds}s` : ''
    return `+${hours}h${minutesPart || secondsPart ? minutesPart || secondsPart : ''}`
  }

  if (minutes > 0) {
    if (seconds === 0) return `+${minutes}min`
    return `+${minutes}:${String(seconds).padStart(2, '0')}min`
  }

  return `+${seconds}s`
}
</script>

<template>
  <div>
    <EventSwitcher />
    <h1>Wyniki dynamiczne</h1>
    <p class="text-muted dynamic-explainer">
      Kolejność pokazuje potencjalne miejsca na podstawie tempa zawodników wciąż na trasie.
      Zawodnik, który dotarł do mniejszej liczby punktów pomiaru czasu, ale szybciej, pozostaje wyżej w tabeli,
      dopóki jego obecny czas na trasie jest niższy niż wynik konkurenta na kolejnym punkcie.
    </p>

    <!-- Search functionality -->
    <GenderFilter v-model="genderFilter" />
    <div class="search-container mb-3">
      <div class="position-relative">
        <div class="search-input-wrapper">
          <svg class="search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" fill="currentColor"/>
          </svg>
          <input
            type="text"
            v-model="searchQuery"
            class="form-control search-input"
            placeholder="Wyszukaj zawodnika po numerze lub nazwie..."
            @focus="showDropdown = searchQuery.trim().length > 0 && filteredResults.length > 0"
          />
        </div>

        <div v-if="showDropdown" class="search-dropdown">
          <div
            v-for="result in filteredResults"
            :key="result.id"
            class="search-dropdown-item"
            @click="selectResult(result)"
          >
            <span class="badge bg-secondary me-2">{{ result.number }}</span>
            <span>{{ result.name }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="table-scroll">
        <div class="settings">
            <span class="badge bg-info">Pozycje aktualizowane na żywo</span>
            <span class="badge bg-success">Czas: {{ new Date(now).toLocaleTimeString() }}</span>
            <input type="checkbox" v-model="showElapsedTime" class="form-check-input" />
            <label class="form-check-label">Czas narastająco</label>
        </div>
      <table class="table table-striped">
        <thead>
          <tr>
            <th>#</th>
            <th>Zawodnik</th>
            <th v-if="resultsStore.shouldShowCategoryColumn()">kat.</th>
            <th v-if="resultsStore.shouldShowPointsColumn()">Punkty</th>
            <th>Start</th>
            <th v-for="marker in getMarkersAsArray()" :key="marker.id" class="marker-column">
                <span v-html="formatMarkerText(marker.text)"></span>
            </th>
            <th v-if="!isLastMarkerFinish()">Meta</th>
            <th>Czas</th>
            <th>Strata</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(result, index) in rankedResults"
            :key="result.id"
            :data-result-id="result.id"
            :class="{ 'row-changed': recentlyChanged[result.id] }"
          >
            <td>
              {{ index + 1 }}
              <span
                v-if="positionChanges[result.id]"
                :class="['position-arrow', positionChanges[result.id].dir === 'up' ? 'position-up' : 'position-down']"
              >{{ positionChanges[result.id].dir === 'up' ? '▲' : '▼' }}</span>
            </td>
            <td><span class="badge bg-secondary">{{ result.number }}</span> {{ result.name }}</td>
            <td v-if="resultsStore.shouldShowCategoryColumn()">
              {{ result.categoryPosition }}<span v-if="result.genderPosition" class="text-muted"> · {{ result.genderPosition }}</span>
            </td>
            <td v-if="resultsStore.shouldShowPointsColumn()">
              {{ result.pointScore || 0 }}
            </td>
            <td>{{ result.start_time ? new Date(result.start_time).toLocaleTimeString() : '' }}</td>
            <td v-for="(marker, index) in getMarkersAsArray()" :key="marker.id" class="marker-cell">
              <span v-if="index == getMarkersAsArray().length - 1 && isLastMarkerFinish()">
                  {{ result.finish_time ? new Date(result.finish_time).toLocaleTimeString() : (result.finish_time_gps ? new Date(result.finish_time_gps).toLocaleTimeString() : '')}}
                  <span v-if="!result.finish_time && result.finish_time_gps" class="badge bg-warning">GPS</span>
              </span>
                <span v-else-if="result.splits[marker.id]">
                    <span v-if="showElapsedTime">
                        {{ result.splits[marker.id].time ? getHHMMSSString(timeFromStartToSplit(result, result.splits[marker.id])) : '--:--:--' }}
                    </span>
                    <span v-else>
                        {{ result.splits[marker.id].time ? new Date(result.splits[marker.id].time).toLocaleTimeString() : '--:--:--' }}
                    </span>
                </span>
            </td>
            <td v-if="!isLastMarkerFinish()" class="marker-cell">
              {{ result.finish_time ? new Date(result.finish_time).toLocaleTimeString() : (result.finish_time_gps ? new Date(result.finish_time_gps).toLocaleTimeString() : '')}}
              <span v-if="!result.finish_time && result.finish_time_gps" class="badge bg-warning">GPS</span>
            </td>
            <td>
              <span v-if="result.dnf || result.abs" :class="getResultClass(result)">{{ result.abs? "DNS" : "DNF" }}</span>
              <span v-else :class="getResultClass(result)">
                {{ result.finish_time ? result.timeScore : (result.finish_time_gps ? result.timeScore : (result.start_time ? calculateTimeFromStart(result) : '--:--:--')) }}
              </span>
              <span v-if="isRunnerLive(result)" class="badge bg-light text-dark border live-badge" title="Zawodnik wciąż na trasie - czas i pozycja są szacunkowe">
                na trasie
              </span>
              <span
                v-if="result.penaltySeconds"
                class="badge bg-warning text-dark penalty-badge"
                :title="result.penaltyComment || 'Kara czasowa'"
              >
                {{ formatPenaltyLabel(result.penaltySeconds) }}
              </span>
            </td>
            <td>
              {{ formatGapTime(resultsStore.getGapToLeader(result)) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.table {
    text-align: left;
}

.dynamic-explainer {
  max-width: 60rem;
}

.marker-column {
    text-align: center;
}

.table td.marker-cell {
    text-align: center;
}
.result-upcoming {
  font-style: italic;
}
.table-scroll {
  width: 100%;
  max-height: 80vh;
  overflow-x: auto;
  overflow-y: auto;
}

.table thead th {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #f8f9fa;
}
.table.table-striped > tbody > tr.row-changed > td,
tbody tr.row-changed > td {
  background: #ebd0b2 !important;
  transition: background 1s;
  font-weight: bold !important;
  animation: highlight-row 1s linear;
}
@keyframes highlight-row {
  0% { background: #ffa754; }
  100% { background: #ebd0b2; }
}

.position-arrow {
  margin-left: 4px;
  font-size: 0.75rem;
}
.position-up {
  color: #198754;
}
.position-down {
  color: #dc3545;
}

.live-badge {
  margin-left: 6px;
  vertical-align: middle;
  font-style: italic;
}

.search-container {
  max-width: 400px;
}

.search-input-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  pointer-events: none;
  z-index: 10;
}

.search-input {
  padding-left: 40px;
}

.search-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #dee2e6;
  border-top: none;
  border-radius: 0 0 0.375rem 0.375rem;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.search-dropdown-item {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  border-bottom: 1px solid #f8f9fa;
}

.search-dropdown-item:hover {
  background-color: #f8f9fa;
}

.search-dropdown-item:last-child {
  border-bottom: none;
}

.penalty-badge {
  margin-left: 6px;
  vertical-align: middle;
}
</style>
